// src/config/pageAccess.js
//
// Single source of truth for page-level access across the app.
//
// Access is decided in two layers:
//   1. Role default — each page lists the roles that can reach it (mirrors the `allowedRoles`
//      on the <Route> elements in src/App.js). `roles: null` means every signed-in user.
//   2. Per-employee override — a `pageOverrides` map on the user's `users/{uid}` document,
//      shaped { [pageKey]: true | false }. `true` grants access even if the role default would
//      deny; `false` denies access even if the role default would allow. An absent key falls
//      back to the role default. Managers edit these on the Team Access page.
//
// IMPORTANT: if you change a route's allowedRoles in App.js, update the matching entry here so
// route enforcement (ProtectedRoute), the sidebar (Layout), and the Team Access page all agree.

export const CATEGORIES = {
  general: { en: 'General', ar: 'عام' },
  team: { en: 'Team & Employees', ar: 'الفريق والموظفون' },
  clients: { en: 'Clients & Campaigns', ar: 'العملاء والحملات' },
  social: { en: 'Social Media', ar: 'السوشيال ميديا' },
  finance: { en: 'Invoices & Finance', ar: 'الفواتير والمالية' },
  reports: { en: 'Reports & Oversight', ar: 'التقارير والإشراف' },
  tools: { en: 'Tools & Resources', ar: 'الأدوات والموارد' },
};

// Every role known to the app (for the role-reference matrix and role filter).
export const ALL_ROLES = ['Manager', 'Sales Manager', 'Accounts Manager', 'Sales', 'Content Creator', 'HR', 'Supervisor', 'Trainee', 'Employee', 'Freelancer'];

export const ROLE_LABELS = {
  en: {
    Manager: 'Manager', 'Sales Manager': 'Sales Manager', 'Accounts Manager': 'Accounts Manager', Sales: 'Sales',
    'Content Creator': 'Content Creator', HR: 'HR', Supervisor: 'Supervisor',
    Trainee: 'Trainee', Employee: 'Employee', Freelancer: 'Freelancer',
  },
  ar: {
    Manager: 'مدير', 'Sales Manager': 'مدير مبيعات', 'Accounts Manager': 'مدير حسابات', Sales: 'مبيعات',
    'Content Creator': 'صانع محتوى', HR: 'موارد بشرية', Supervisor: 'مشرف',
    Trainee: 'متدرب', Employee: 'موظف', Freelancer: 'فريلانسر',
  },
};

export const roleLabel = (role, language) => ROLE_LABELS[language]?.[role] || role || '';

// Mirror of App.js route access. `prefixes` are URL prefixes used to map a live pathname back to
// this page (a page's sub-routes share its key). `roles: null` => any signed-in user.
export const PAGE_CATALOG = [
  // General
  { key: 'dashboard', category: 'general', path: '/dashboard', prefixes: ['/dashboard'], en: 'Dashboard', ar: 'لوحة التحكم', roles: null },
  { key: 'tasks', category: 'general', path: '/task/new', prefixes: ['/task'], en: 'Tasks (create / view / edit)', ar: 'المهام (إنشاء / عرض / تعديل)', roles: null },
  { key: 'daily-log', category: 'general', path: '/daily-log', prefixes: ['/daily-log'], en: 'Daily Log', ar: 'السجل اليومي', roles: null },
  { key: 'chat-internal', category: 'general', path: '/growup-chat', prefixes: ['/growup-chat'], en: 'Team Chat (GrowUp Connect)', ar: 'محادثة الفريق', roles: null },
  { key: 'notifications', category: 'general', path: '/notifications', prefixes: ['/notifications'], en: 'Notifications', ar: 'الإشعارات', roles: null },

  // Team & Employees
  { key: 'employees', category: 'team', path: '/employees', prefixes: ['/employees'], en: 'Employees (manage)', ar: 'الموظفون (إدارة)', roles: ['Manager', 'HR', 'Accounts Manager'] },
  { key: 'trainees', category: 'team', path: '/trainees', prefixes: ['/trainees'], en: 'Trainees (manage)', ar: 'المتدربون (إدارة)', roles: ['Manager', 'Accounts Manager'] },
  { key: 'trainee-tasks', category: 'team', path: '/trainee-tasks-dashboard', prefixes: ['/trainee-tasks-dashboard'], en: 'Trainee Tasks Dashboard', ar: 'لوحة مهام المتدربين', roles: ['Manager', 'Accounts Manager', 'Trainee', 'Supervisor'] },

  // Clients & Campaigns
  { key: 'clients', category: 'clients', path: '/clients', prefixes: ['/clients'], en: 'Clients (manage)', ar: 'العملاء (إدارة)', roles: ['Manager', 'Sales Manager', 'Sales', 'Accounts Manager'] },
  { key: 'client-profitability', category: 'clients', path: '/client-profitability', prefixes: ['/client-profitability'], en: 'Client Profitability', ar: 'ربحية العملاء', roles: ['Manager', 'Sales Manager', 'Accounts Manager'] },
  { key: 'work-by-person', category: 'clients', path: '/work-by-person', prefixes: ['/work-by-person'], en: 'Work by Person', ar: 'العمل حسب الشخص', roles: ['Manager', 'Sales Manager'] },
  { key: 'campaigns', category: 'clients', path: '/campaigns', prefixes: ['/campaigns'], en: 'Campaigns', ar: 'الحملات', roles: ['Manager', 'Sales Manager', 'Sales', 'Accounts Manager', 'Content Creator'] },
  { key: 'client-weekly-work', category: 'clients', path: '/client-weekly-work', prefixes: ['/client-weekly-work'], en: 'Client Weekly Work', ar: 'أعمال العملاء الأسبوعية', roles: ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'] },
  { key: 'ai-chat', category: 'clients', path: '/chat', prefixes: ['/chat'], en: 'AI Chat', ar: 'محادثة الذكاء الاصطناعي', roles: ['Manager', 'Sales Manager', 'Sales', 'Accounts Manager', 'Content Creator'] },

  // Social Media
  { key: 'social-dashboard', category: 'social', path: '/social/dashboard', prefixes: ['/social/dashboard'], en: 'Social Dashboard', ar: 'لوحة السوشيال ميديا', roles: ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'] },
  { key: 'social-pages', category: 'social', path: '/social/pages', prefixes: ['/social/pages'], en: 'Client Social Pages', ar: 'صفحات السوشيال للعملاء', roles: ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'] },
  { key: 'social-tasks', category: 'social', path: '/social/tasks', prefixes: ['/social/tasks'], en: 'Content Tasks', ar: 'مهام المحتوى', roles: ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales', 'Content Creator'] },
  { key: 'social-calendar', category: 'social', path: '/social/calendar', prefixes: ['/social/calendar'], en: 'Content Calendar', ar: 'تقويم المحتوى', roles: ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'] },
  { key: 'social-approvals', category: 'social', path: '/social/approvals', prefixes: ['/social/approvals'], en: 'Content Approvals', ar: 'موافقات المحتوى', roles: ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'] },
  { key: 'social-reports', category: 'social', path: '/social/reports', prefixes: ['/social/reports'], en: 'Social Reports', ar: 'تقارير السوشيال', roles: ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'] },

  // Invoices & Finance
  { key: 'invoices', category: 'finance', path: '/invoices', prefixes: ['/invoices'], en: 'Invoices', ar: 'الفواتير', roles: ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales'] },
  { key: 'cost-settings', category: 'finance', path: '/cost-settings', prefixes: ['/cost-settings'], en: 'Costs & Rates', ar: 'التكاليف والأسعار', roles: ['Manager'] },

  // Reports & Oversight
  { key: 'staff-performance', category: 'reports', path: '/staff-performance', prefixes: ['/staff-performance'], en: 'Staff Performance', ar: 'أداء الطاقم', roles: ['Manager'] },
  { key: 'team-performance', category: 'reports', path: '/team-performance', prefixes: ['/team-performance'], en: 'Team Performance', ar: 'أداء الفريق', roles: ['Manager'] },
  { key: 'trainee-performance', category: 'reports', path: '/trainee-performance', prefixes: ['/trainee-performance'], en: 'Trainee Report', ar: 'تقرير المتدربين', roles: ['Manager'] },
  { key: 'manager-daily-logs', category: 'reports', path: '/manager/daily-logs', prefixes: ['/manager/daily-logs'], en: 'Daily Logs (all staff)', ar: 'السجلات اليومية (كل الطاقم)', roles: ['Manager'] },
  { key: 'attendance', category: 'reports', path: '/attendance-report', prefixes: ['/attendance-report'], en: 'Attendance Report', ar: 'تقرير الحضور', roles: ['Manager'] },
  { key: 'login-history', category: 'reports', path: '/login-history', prefixes: ['/login-history'], en: 'Login History', ar: 'سجل الدخول', roles: ['Manager'] },
  { key: 'login-map', category: 'reports', path: '/login-map', prefixes: ['/login-map'], en: 'Login Map', ar: 'خريطة الدخول', roles: ['Manager'] },
  { key: 'promotions', category: 'reports', path: '/promotions', prefixes: ['/promotions'], en: 'Promotions Calculator', ar: 'حاسبة الترقيات', roles: ['Manager'] },
  { key: 'work-hours', category: 'reports', path: '/work-hours', prefixes: ['/work-hours'], en: 'Work Hours Management', ar: 'إدارة ساعات العمل', roles: ['Manager'] },

  // Tools & Resources
  { key: 'tools', category: 'tools', path: '/tools', prefixes: ['/tools'], en: 'Tools (QR / Image tools)', ar: 'الأدوات (QR / أدوات الصور)', roles: ['Manager', 'Accounts Manager', 'Sales Manager', 'Sales', 'HR'] },
  { key: 'events', category: 'tools', path: '/events', prefixes: ['/events'], en: 'Events', ar: 'الفعاليات', roles: ['Manager', 'Accounts Manager'] },
  { key: 'resources', category: 'tools', path: '/resources', prefixes: ['/resources'], en: 'Company Resources', ar: 'موارد الشركة', roles: ['Manager', 'Accounts Manager'] },
];

// Quick lookup by key.
export const PAGE_BY_KEY = PAGE_CATALOG.reduce((acc, p) => {
  acc[p.key] = p;
  return acc;
}, {});

// Does a role satisfy a page's role default (ignoring overrides)?
export const roleCanAccess = (page, role) => {
  if (!page) return true;
  if (page.roles === null) return true;
  return !!role && page.roles.includes(role);
};

/**
 * Effective access for a page: per-employee override wins, otherwise the role default.
 * @param {string} pageKey
 * @param {string} role
 * @param {object} overrides  users/{uid}.pageOverrides ({ [key]: true|false })
 */
export const canAccessPage = (pageKey, role, overrides) => {
  const ov = overrides ? overrides[pageKey] : undefined;
  if (ov === true) return true;
  if (ov === false) return false;
  return roleCanAccess(PAGE_BY_KEY[pageKey], role);
};

/**
 * Map a live pathname to a page key by longest-matching prefix. Returns null if the path isn't a
 * catalogued page (e.g. /profile, /account-disabled) — callers should treat null as "no override
 * applies" and fall back to the route's own rules.
 */
export const matchPageKey = (pathname) => {
  if (!pathname) return null;
  const clean = pathname.split('?')[0];
  let best = null;
  let bestLen = -1;
  PAGE_CATALOG.forEach((p) => {
    (p.prefixes || [p.path]).forEach((prefix) => {
      if ((clean === prefix || clean.startsWith(prefix + '/')) && prefix.length > bestLen) {
        best = p.key;
        bestLen = prefix.length;
      }
    });
  });
  return best;
};
